import { computed, inject, Injectable, signal } from '@angular/core';
import {
  CostForecastResponse,
  HttpError,
  PriceForecastDetailsResponse,
} from '../../../core/clients/omni-core/models';
import { Api } from '../../../core/clients/omni-core/api';
import { OrganizationsStore } from '../../organizations/store/organizations.store';
import {
  getSupplierContractCostForecast,
  getSupplierContractPriceForecast,
} from '../../../core/clients/omni-core/functions';

export type ForecastKey = `${string}-${number}-${string | 'ALL'}`;

export interface SupplierContractPriceState {
  supplierContractId: string;
  year: number;
  energyInterfaceId: string | null;

  data: PriceForecastDetailsResponse | null;

  isLoading: boolean;
  error: HttpError | unknown | null;
}

export interface SupplierContractCostState {
  supplierContractId: string;
  year: number;
  energyInterfaceId: string | null;

  data: CostForecastResponse | null;

  isLoading: boolean;
  error: HttpError | unknown | null;
}

@Injectable()
export class SupplierContractForecastStore {
  private api = inject(Api);
  private organizationsStore = inject(OrganizationsStore);

  organizationId = computed(() => this.organizationsStore.selectedId());

  #prices = signal<Record<ForecastKey, SupplierContractPriceState | undefined>>({});
  #cost = signal<Record<ForecastKey, SupplierContractCostState | undefined>>({});

  prices = computed(() => this.#prices());
  cost = computed(() => this.#cost());

  private patchPrice(
    supplierContractId: string,
    year: number,
    energyInterfaceId: string | null,
    data: Partial<SupplierContractPriceState>,
  ) {
    const key: ForecastKey = `${supplierContractId}-${year}-${energyInterfaceId ?? 'ALL'}`;
    this.#prices.update((prices) => {
      const currentPrice: SupplierContractPriceState = prices[key] ?? {
        supplierContractId,
        year,
        energyInterfaceId,
        data: null,
        isLoading: false,
        error: null,
      };
      return { ...prices, [key]: { ...currentPrice, ...data } };
    });
  }

  private patchCost(
    supplierContractId: string,
    year: number,
    energyInterfaceId: string | null,
    data: Partial<SupplierContractCostState>,
  ) {
    const key: ForecastKey = `${supplierContractId}-${year}-${energyInterfaceId ?? 'ALL'}`;
    this.#cost.update((cost) => {
      const currentCost: SupplierContractCostState = cost[key] ?? {
        supplierContractId,
        year,
        energyInterfaceId,
        data: null,
        isLoading: false,
        error: null,
      };
      return { ...cost, [key]: { ...currentCost, ...data } };
    });
  }

  async loadPrice(supplierContractId: string, year: number, energyInterfaceId: string | null) {
    this.patchPrice(supplierContractId, year, energyInterfaceId, { isLoading: true, error: null });
    try {
      const oid = this.organizationId();
      if (!oid) {
        throw new Error('Organization not selected');
      }
      const data = await this.api.invoke(getSupplierContractPriceForecast, {
        id: supplierContractId,
        year,
        energyInterfaceID: energyInterfaceId ?? undefined,
        oid,
      });
      this.patchPrice(supplierContractId, year, energyInterfaceId, { data, isLoading: false });
    } catch (error) {
      this.patchPrice(supplierContractId, year, energyInterfaceId, { error, isLoading: false });
    }
  }

  async loadCost(supplierContractId: string, year: number, energyInterfaceId: string | null) {
    this.patchCost(supplierContractId, year, energyInterfaceId, { isLoading: true, error: null });
    try {
      const oid = this.organizationId();
      if (!oid) {
        throw new Error('Organization not selected');
      }
      const data = await this.api.invoke(getSupplierContractCostForecast, {
        id: supplierContractId,
        year,
        energyInterfaceID: energyInterfaceId ?? undefined,
        oid,
      });
      this.patchCost(supplierContractId, year, energyInterfaceId, { data, isLoading: false });
    } catch (error) {
      this.patchCost(supplierContractId, year, energyInterfaceId, { error, isLoading: false });
    }
  }

  reset() {
    this.#cost.set({});
    this.#prices.set({});
  }
}
